import { useEffect, useState } from "react";
import { Banknote, CalendarClock, CheckCircle2, LockKeyhole, MessageSquare, Video } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import VideoPreparationNotice from "@/components/video/VideoPreparationNotice";
import ManualPaymentGate from "@/components/video/ManualPaymentGate";

const capitalize = (value) => value ? value.charAt(0).toUpperCase() + value.slice(1) : value;
const dateLabel = (value) => capitalize(new Intl.DateTimeFormat("da-DK", { weekday: "long", day: "numeric", month: "long" }).format(new Date(value)));
const timeLabel = (value) => new Intl.DateTimeFormat("da-DK", { hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date(value));
const durationMinutes = (seconds) => Math.max(1, Math.round((seconds || 0) / 60));

function PaymentGate({ problem, role, onPaid }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const terms = problem.accepted_terms || {};
  const amount = terms.price_model === "hourly"
    ? terms.estimated_price_max || terms.estimated_price_min
    : terms.fixed_price;
  const amountLabel = amount ? `${Number(amount).toLocaleString("da-DK")} kr.` : "det aftalte beløb";

  useEffect(() => {
    if (!["pending", "awaiting_payment"].includes(problem.payment_status)) return undefined;
    const check = () => api.get(`/problems/${problem.id}`)
      .then((response) => {
        if (response.data.payment_status === "paid") onPaid(response.data);
      })
      .catch(() => {});
    const timer = window.setInterval(check, 5000);
    return () => window.clearInterval(timer);
  }, [onPaid, problem.id, problem.payment_status]);

  const startPayment = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/problems/${problem.id}/checkout`);
      if (data.paid) {
        onPaid({ ...problem, payment_status: "paid" });
        return;
      }
      if (data.url) window.location.assign(data.url);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Betalingen kunne ikke startes.");
      setBusy(false);
    }
  };

  return <section className="video-next-step" data-testid="payment-gate">
    <div className="video-next-step__confirmed"><LockKeyhole size={18}/><span>Næste trin · betaling</span></div>
    <div className="video-entry-card">
      <div className="video-entry-card__icon"><Banknote/></div>
      <div className="video-entry-card__body">
        {role === "customer" ? <>
          <h3>Betal {amountLabel} sikkert til SpørgMig</h3>
          <p>Beløbet holdes af SpørgMig mens opgaven gennemføres. Når betalingen er registreret, kan I aftale et konkret tidspunkt.</p>
          <button type="button" className="btn-primary" disabled={busy} onClick={startPayment} data-testid="start-problem-payment">
            {busy ? "Åbner betaling…" : `Betal ${amountLabel}`}
          </button>
        </> : <>
          <h3>Afventer kundens betaling</h3>
          <p>Kunden betaler til SpørgMig. Du skal ikke sende betalingsoplysninger eller bekræfte betalingen. Når betalingen er registreret, kan I aftale tidspunktet.</p>
        </>}
        {error && <p className="video-inline-error" role="alert">{error}</p>}
      </div>
    </div>
  </section>;
}

function PostCallState({ problem, session, role, onUpdated, onOpenChat }) {
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState("");
  const completed = session.completion_status === "completed" || session.problem_status === "completed";
  const completeJob = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post(`/problems/${problem.id}/complete`, { confirmed: true });
      onUpdated(data);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Opgaven kunne ikke færdigmarkeres.");
    } finally { setBusy(false); setConfirming(false); }
  };

  return <section className="video-next-step video-post-call" data-testid="video-post-call-state">
    <div className="video-next-step__confirmed"><CheckCircle2 size={18}/><span>Videosamtalen er afsluttet</span></div>
    <div className="video-entry-card">
      <div className="video-entry-card__icon"><CheckCircle2/></div>
      <div className="video-entry-card__body">
        <h3>{role === "expert" ? `Samtaletid: ${durationMinutes(session.actual_duration_seconds)} min.` : `I talte sammen i ${durationMinutes(session.actual_duration_seconds)} min.`}</h3>
        {!completed && role === "expert" && <>
          <p>Markér først opgaven som færdig, når den aftalte hjælp er leveret. Kunden har fortsat adgang til chatten.</p>
          {!confirming ? <button type="button" className="btn-primary" onClick={() => setConfirming(true)}>Markér opgave som færdig</button> : <div className="video-post-call__confirm">
            <h4>Er opgaven færdig?</h4>
            <p>Når du bekræfter, afsluttes opgaven. Kunden kan derefter anmelde forløbet.</p>
            <div className="video-post-call__actions"><button type="button" className="btn-secondary" onClick={() => setConfirming(false)}>Annuller</button><button type="button" className="btn-primary" disabled={busy} onClick={completeJob}>{busy ? "Gemmer…" : "Bekræft færdig"}</button></div>
          </div>}
        </>}
        {!completed && role === "customer" && <><h4>Opgaven er fortsat aktiv</h4><p>Eksperten færdigmarkerer opgaven, når den aftalte hjælp er leveret. I kan fortsætte dialogen i chatten.</p></>}
        {completed && <>
          <h4>Opgaven er afsluttet</h4>
          <p>{role === "expert" ? problem.payment_method === "manual_expert" ? "Opgaven er afsluttet. Betalingen er modtaget direkte på din konto. Et eventuelt platformgebyr afregnes separat med SpørgMig." : "Opgaven er afsluttet. SpørgMig frigiver betalingen efter den gældende afslutningsperiode." : "Eksperten har markeret den aftalte hjælp som leveret. Kontakt SpørgMig via chatten, hvis noget ikke stemmer."}</p>
        </>}
        {(role === "customer" || !completed) && (onOpenChat ? <button type="button" className="btn-secondary" onClick={onOpenChat}><MessageSquare size={15}/>Gå til chatten</button> : <a href="#chat" className="btn-secondary"><MessageSquare size={15}/>Gå til chatten</a>)}
        {error && <p className="video-inline-error" role="alert">{error}</p>}
      </div>
    </div>
  </section>;
}

export default function VideoConsultationEntry({ problem, role, onProblemUpdated, onOpenChat }) {
  const [session, setSession] = useState(null);
  const [error, setError] = useState("");
  const [paymentStatus, setPaymentStatus] = useState(problem?.payment_status);
  const state = problem?.consultation_schedule_state || "no_schedule";
  const activeAgreement = problem?.agreement_status === "active" && ["confirmed", "ready_now"].includes(state);
  const completedPostCall = problem?.status === "completed";
  const paymentPending = paymentStatus !== "paid";
  const eligible = (activeAgreement && !paymentPending) || completedPostCall;

  useEffect(() => {
    setPaymentStatus(problem?.payment_status);
  }, [problem?.id, problem?.payment_status]);

  useEffect(() => {
    if (!eligible) { setSession(null); return undefined; }
    const load = () => api.get(`/problems/${problem.id}/video-session`)
      .then((response) => { setSession(response.data); setError(""); })
      .catch((requestError) => setError(requestError.response?.data?.detail || "Videostatus kunne ikke hentes."));
    load(); const timer = setInterval(load, 10000); return () => clearInterval(timer);
  }, [eligible, problem?.id]);

  if (paymentPending && problem?.agreement_status === "active" && problem?.status === "offer_accepted") {
    const Gate = problem.payment_method === "manual_expert" ? ManualPaymentGate : PaymentGate;
    return <Gate
      problem={{ ...problem, payment_status: paymentStatus }}
      role={role}
      onOpenChat={onOpenChat}
      onPaid={(payment) => {
        setPaymentStatus("paid");
        onProblemUpdated?.({
          problem_status: problem.status,
          completion_status: problem.completion_status,
          funds_state: payment.funds_state || "held",
          funds_release_at: problem.funds_release_at,
          payment_status: "paid",
        });
      }}
    />;
  }
  if (!eligible) return null;
  if (session?.lifecycle_state === "ended" || session?.status === "ended") {
    return <PostCallState problem={problem} session={session} role={role} onOpenChat={onOpenChat} onUpdated={(nextSession) => { setSession(nextSession); onProblemUpdated?.(nextSession); }}/>;
  }

  const immediate = state === "ready_now";
  const prejoinOpen = immediate || (session && session.status !== "scheduled");
  return <section className="video-next-step" data-testid="video-consultation-entry">
    <div className="video-next-step__confirmed"><CheckCircle2 size={18}/><span>{immediate ? "Videosamtale klar" : "Videosamtale bekræftet"}</span></div>
    <div className="video-entry-card">
      <div className="video-entry-card__icon"><Video/></div>
      <div className="video-entry-card__body">
        {immediate ? <><h3>I er begge klar til at tage samtalen nu.</h3><p>Begge deltagere gennemfører først kamera- og mikrofontesten.</p></> : <>
          <div className="video-meeting-date">{dateLabel(problem.scheduled_at)}</div>
          <div className="video-meeting-time">kl. {timeLabel(problem.scheduled_at)}</div>
          <p>Forventet varighed: {problem.duration_min || 15} min.</p>
          {session?.status === "scheduled" && <p>Mødet åbner kl. {timeLabel(session.prejoin_opens_at)}. Du kan gå ind 15 minutter før for at teste kamera og mikrofon.</p>}
        </>}
        {session?.current_user_ready && !session?.both_ready && <p>Du er klar. Venter på {session.other_participant?.name}.</p>}
        {error && <p className="video-inline-error">{error}</p>}
      </div>
      {!session ? <span className="video-entry-disabled">Henter videostatus…</span> : prejoinOpen ? <Link to={`/videosamtale/${problem.id}`} className="btn-primary" data-testid="enter-video-consultation">Gå til videosamtale</Link> : <button type="button" disabled className="video-entry-disabled" data-testid="enter-video-consultation-disabled"><CalendarClock size={15}/>Gå til videosamtale</button>}
    </div>
    <VideoPreparationNotice compact/>
  </section>;
}
